// How a sauna's woods lie on its faces in 3D (docs/claude/woods.md): which side
// of a wall a face looks into, and the texture coordinates that lay a wood's
// boards on it. Pure and DOM-free: the 3D view hands it a mesh's points in the
// room's LOCAL frame (x, y in mm, z above the room's floor) and the normal of
// the face each one is on.

import type { Vec2 } from '../shared/types';
import { pointInPolygon } from './geometry';

export type P3 = readonly [number, number, number];

/** A wood image's size on a surface (mm), and its boards across its height. */
export interface WoodImg { w: number; h: number; boards: number }

/** How far off a face its side is probed (mm): past an arc's chord error, well
 *  short of any wall's thickness. */
const PROBE = 5;

/** A face that looks this far up or down is level (a top, a sill, a soffit). */
const LEVEL = 0.7;

/** The side of the room's walls a face looks into: `out` when a point a little
 *  off it along its normal is outside the room's outer outline, else `in` —
 *  the inner faces, a partition's both, a jamb, and every level face. */
export function faceSide(outer: readonly Vec2[], c: P3, n: P3): 'in' | 'out' {
  if (Math.abs(n[2]) > LEVEL) return 'in';
  const l = Math.hypot(n[0], n[1]) || 1;
  return pointInPolygon({ x: c[0] + (n[0] / l) * PROBE, y: c[1] + (n[1] / l) * PROBE }, outer as Vec2[]) ? 'in' : 'out';
}

/** A face point's coordinates on its surface (mm): on an upright face along it
 *  and up (z, so boards line up from piece to piece round the room), on a level
 *  one along `axes.u` and `axes.n`. */
export function surfaceMm(p: P3, n: P3, axes: { u: Vec2; n: Vec2 }): [number, number] {
  if (Math.abs(n[2]) > LEVEL) return [p[0] * axes.u.x + p[1] * axes.u.y, p[0] * axes.n.x + p[1] * axes.n.y];
  const l = Math.hypot(n[0], n[1]) || 1;
  return [(-n[1] * p[0] + n[0] * p[1]) / l, p[2]];
}

/** Surface mm in a wood's image: its boards (the image's x) along the first
 *  coordinate, or along the second when `turn`. */
export function imageUV(mm: readonly [number, number], img: WoodImg, turn: boolean): [number, number] {
  return turn ? [mm[1] / img.w, mm[0] / img.h] : [mm[0] / img.w, mm[1] / img.h];
}

/** A mesh's UVs, already true mm on its surface (a sculpted bench's: along the
 *  bench, round its profile), laid in a wood's image: its boards along the first,
 *  so the grain follows the bench round a corner with no seam. */
export function ownImageUVs(uv: ArrayLike<number>, img: WoodImg): number[] {
  const out: number[] = [];
  for (let i = 0; i + 1 < uv.length; i += 2) out.push(...imageUV([uv[i], uv[i + 1]], img, false));
  return out;
}

/** A bench piece's UVs, one per point: ONE board of the image (the `k`th, from
 *  its top) laid along the piece — its plan points' principal axis — and across
 *  it, the piece's width on its top and bottom, its height on its sides, inside
 *  the board's middle so no seam of the image crosses the piece. A face more
 *  than a board and a half across (a bench with no slats, a skirting) takes the
 *  image at its own scale instead, seams and all, as boards side by side. `k`
 *  also moves where along the board the piece starts, so pieces side by side
 *  differ. */
export function boardUVs(pts: readonly P3[], nrm: readonly P3[], img: WoodImg, k: number): number[] {
  const m = pts.length;
  if (!m) return [];
  let mx = 0, my = 0;
  for (const p of pts) { mx += p[0]; my += p[1]; }
  mx /= m; my /= m;
  let sxx = 0, syy = 0, sxy = 0;
  for (const p of pts) { const dx = p[0] - mx, dy = p[1] - my; sxx += dx * dx; syy += dy * dy; sxy += dx * dy; }
  const th = 0.5 * Math.atan2(2 * sxy, sxx - syy), d = { x: Math.cos(th), y: Math.sin(th) }, q = { x: -d.y, y: d.x };
  const along = (p: P3): number => p[0] * d.x + p[1] * d.y, across = (p: P3): number => p[0] * q.x + p[1] * q.y;
  const range = (f: (p: P3) => number): [number, number] => {
    let lo = Infinity, hi = -Infinity;
    for (const p of pts) { const v = f(p); if (v < lo) lo = v; if (v > hi) hi = v; }
    return [lo, hi];
  };
  const [d0] = range(along), [q0, q1] = range(across), [z0, z1] = range((p) => p[2]);
  const bw = img.h / img.boards;
  const board = ((k % img.boards) + img.boards) % img.boards, start = ((k * 0.381966) % 1) * img.w;
  // Across a face from `lo` to `hi`: inside the board, or at the image's scale.
  const v = (x: number, lo: number, hi: number): number => (hi - lo > 1.5 * bw
    ? 1 - (board * bw + x - lo) / img.h
    : 1 - (board + 0.15 + 0.7 * (hi - lo > 1e-6 ? (x - lo) / (hi - lo) : 0.5)) / img.boards);
  const out: number[] = [];
  for (let i = 0; i < m; i++) {
    const p = pts[i], n = nrm[i];
    if (Math.abs(n[2]) > LEVEL) out.push((along(p) - d0 + start) / img.w, v(across(p), q0, q1));
    else if (Math.abs(n[0] * q.x + n[1] * q.y) >= Math.abs(n[0] * d.x + n[1] * d.y)) out.push((along(p) - d0 + start) / img.w, v(p[2], z0, z1));
    else out.push((across(p) - q0 + start) / img.w, v(p[2], z0, z1)); // an end: its end grain drawn as a side
  }
  return out;
}
